/**
 * Client-side image compression for chat uploads.
 *
 * Why: phone photos are routinely 4-12MB. Encoding to WebP at 1920px wide
 * and ~0.82 quality typically shrinks them to 200-600KB with no visible
 * quality loss at chat-bubble scale. This is the cheapest possible way to
 * cap storage cost — bytes never leave the device.
 *
 * Skips:
 *   - GIFs (would lose animation; canvas only sees the first frame)
 *   - Already-small images (<200KB) — recompression would waste CPU
 *   - SVG / non-raster types
 *
 * On any failure (decode error, OOM, unsupported codec) we fall back to
 * the original file rather than blocking the send.
 */

const MAX_DIMENSION = 1920;
const THUMB_DIMENSION = 256;
const TARGET_TYPE = "image/webp";
const TARGET_QUALITY = 0.82;
const THUMB_QUALITY = 0.72;
/** Skip recompression for files already smaller than this. */
const SKIP_BELOW_BYTES = 200 * 1024;

export interface PreparedImage {
  /** The (possibly compressed) preview file ready to upload. */
  file: File;
  /** Pixel dimensions of `file` after any downscale. */
  width: number;
  height: number;
  /** 256px thumbnail variant. Cheap to load in chat lists / previews
   * before the user opens the lightbox. Absent for tiny inputs / GIFs. */
  thumb?: { file: File; width: number; height: number };
}

/** Decode an arbitrary image File to a bitmap regardless of browser support. */
async function decodeToBitmap(file: File): Promise<ImageBitmap> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // some Safari versions fail on heic/avif — fall through
    }
  }
  // Fallback via <img>
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("decode failed"));
      el.src = url;
    });
    return await createImageBitmap(img);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Encode a bitmap at a target max dimension and quality, returning a File
 * (or null on failure). Aspect ratio preserved.
 */
async function encodeVariant(
  bitmap: ImageBitmap,
  maxDim: number,
  quality: number,
  baseName: string
): Promise<{ file: File; width: number; height: number } | null> {
  const scale = Math.min(1, maxDim / bitmap.width, maxDim / bitmap.height);
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  let canvas: HTMLCanvasElement | OffscreenCanvas;
  if (typeof OffscreenCanvas !== "undefined") {
    canvas = new OffscreenCanvas(w, h);
  } else {
    canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext("2d") as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, w, h);

  let blob: Blob | null = null;
  try {
    if (canvas instanceof OffscreenCanvas) {
      blob = await canvas.convertToBlob({ type: TARGET_TYPE, quality });
    } else {
      blob = await new Promise<Blob | null>((resolve) =>
        (canvas as HTMLCanvasElement).toBlob(resolve, TARGET_TYPE, quality)
      );
    }
  } catch {
    blob = null;
  }
  if (!blob) return null;

  const file = new File([blob], baseName, { type: TARGET_TYPE });
  return { file, width: w, height: h };
}

export async function compressImageForChat(input: File): Promise<PreparedImage> {
  // Animated GIF: DO NOT recompress — would freeze on first frame.
  if (input.type === "image/gif") {
    const bmp = await decodeToBitmap(input).catch(() => null);
    return {
      file: input,
      width: bmp?.width ?? 0,
      height: bmp?.height ?? 0,
    };
  }

  // SVG / unsupported: pass through.
  if (!input.type.startsWith("image/")) {
    return { file: input, width: 0, height: 0 };
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await decodeToBitmap(input);
  } catch {
    return { file: input, width: 0, height: 0 };
  }

  const baseName = input.name.replace(/\.[^.]+$/, "");

  // Tiny file — skip preview recompression but still produce a thumb so
  // chat lists stay snappy.
  let preview: { file: File; width: number; height: number };
  if (input.size < SKIP_BELOW_BYTES) {
    preview = { file: input, width: bitmap.width, height: bitmap.height };
  } else {
    const encoded = await encodeVariant(bitmap, MAX_DIMENSION, TARGET_QUALITY, `${baseName}.webp`);
    if (!encoded) {
      bitmap.close?.();
      return { file: input, width: bitmap.width, height: bitmap.height };
    }
    // If recompression somehow grew the file, keep the original.
    if (encoded.file.size >= input.size) {
      preview = { file: input, width: encoded.width, height: encoded.height };
    } else {
      preview = encoded;
    }
  }

  // Generate the 256px thumbnail. If thumb encoding fails we don't fail
  // the upload — preview alone is enough.
  let thumb: { file: File; width: number; height: number } | undefined;
  // Skip if input is already smaller than the thumb dimension.
  if (Math.max(bitmap.width, bitmap.height) > THUMB_DIMENSION) {
    const t = await encodeVariant(
      bitmap,
      THUMB_DIMENSION,
      THUMB_QUALITY,
      `${baseName}-thumb.webp`
    );
    if (t) thumb = t;
  }
  bitmap.close?.();

  return {
    file: preview.file,
    width: preview.width,
    height: preview.height,
    thumb,
  };
}
